import Popup from '@components/popup';
import css from './index.module.css';
import { toast } from 'sonner';
import axios from 'axios';
import { useState, useEffect } from 'react';
import useUserData from '@hooks/useUserData';

function AddPopupProduct ({ product, showPopup, setShowPopup }: { product: any, showPopup: boolean, setShowPopup: any }) {
    const token = localStorage.getItem('authToken');
    const [unit, setUnit] = useState<'КГ' | 'ШТ'>(() => {
        // Если товар в КГ, устанавливаем КГ; если в ШТ или не указано - ШТ
        if (product?.unit === 'КГ') return 'КГ';
        return 'ШТ';
    });
    const [count, setCount] = useState<number>(() => {
        // Синхронизируем начальное количество с единицей измерения
        if (product?.unit === 'КГ') return 0;
        return 0;
    });
    const [countInput, setCountInput] = useState<string>('');
    const { refreshCartData } = useUserData();

    // Обновляем unit при изменении товара или открытии попапа
    // count обновится автоматически через второй useEffect
    useEffect(() => {
        if (showPopup && product) {
            if (product?.unit === 'КГ') {
                setUnit('КГ');
            } else {
                setUnit('ШТ');
            }
        }
    }, [product?.id, showPopup]);

    // Очищаем поле ввода при открытии попапа
    useEffect(() => {
        if (showPopup) {
            setCountInput('');
            setCount(0);
        }
    }, [showPopup]);

    // При смене единицы измерения очищаем поле ввода
    useEffect(() => {
        if (showPopup) {
            setCountInput('');
            setCount(0);
        }
    }, [unit, showPopup]);

    const handleCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setCountInput(value);
        
        // Позволяем пустое значение для ввода
        if (value === '' || value === '.') {
            return;
        }
        
        const numValue = parseFloat(value);
        if (isNaN(numValue)) return;

        if (unit === 'ШТ') {
            // Для ШТ только целые числа
            const intValue = Math.floor(numValue);
            setCount(intValue);
        } else if (unit === 'КГ') {
            // Для КГ десятичные числа
            setCount(numValue);
        }
    };

    const handleCountBlur = () => {
        // При потере фокуса валидируем и устанавливаем минимальное значение, если поле пустое
        if (countInput === '' || countInput === '.') {
            const minValue = unit === 'ШТ' ? 1 : 0.1;
            setCount(minValue);
            setCountInput(minValue.toString());
            return;
        }
        
        const numValue = parseFloat(countInput);
        if (isNaN(numValue)) {
            const minValue = unit === 'ШТ' ? 1 : 0.1;
            setCount(minValue);
            setCountInput(minValue.toString());
            return;
        }

        if (unit === 'ШТ') {
            // Для ШТ только целые числа, минимум 1
            const intValue = Math.max(1, Math.floor(numValue));
            setCount(intValue);
            setCountInput(intValue.toString());
        } else if (unit === 'КГ') {
            // Для КГ десятичные числа, минимум 0.1
            const kgValue = Math.max(0.1, numValue);
            setCount(kgValue);
            setCountInput(kgValue.toString());
        }
    };

    const adjustCount = (delta: number) => {
        const currentValue = count || (unit === 'ШТ' ? 0 : 0);
        if (unit === 'ШТ') {
            const newValue = Math.max(1, currentValue + delta);
            const intValue = Math.floor(newValue);
            setCount(intValue);
            setCountInput(intValue.toString());
        } else if (unit === 'КГ') {
            const newValue = Math.max(0.1, currentValue + delta);
            // Округляем до 1 знака после запятой
            const roundedValue = Math.round(newValue * 10) / 10;
            setCount(roundedValue);
            setCountInput(roundedValue.toString());
        }
    };

    const addToCart = () => {
        // Валидация перед отправкой
        const finalCount = count || (unit === 'ШТ' ? 1 : 0.1);
        
        if (unit === 'ШТ' && (finalCount < 1 || !Number.isInteger(finalCount))) {
            toast.error('Для штук введите целое число от 1');
            return;
        }
        if (unit === 'КГ' && finalCount < 0.1) {
            toast.error('Для килограмм введите значение от 0.1');
            return;
        }
        const data = {
          prodID: product.id,
          unit,
          count: finalCount
        }
    
        axios.post(`${import.meta.env.VITE_API_URL}/user/cart`, JSON.stringify(data), {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }).then(() => {
          setShowPopup(false);
          toast.success('Товар добавлен в корзину');
          refreshCartData();
        }).catch(() => {
          toast.error('Возникла ошибка, не смогли добавить товар');
        });
    };
    
    return (
      <Popup showPopup={showPopup} setShowPopup={setShowPopup}>
        <div className={css.addPopup}>
          <h1>Добавить в корзину</h1>
          <label>Количество товара</label>
          {unit === 'КГ' ? (
            <>
              {/* <div className={css.quickButtons}>
                {[0.1, 0.2, 0.5, 1, 1.5, 2, 2.5, 3].map((val) => (
                  <button
                    key={val}
                    type="button"
                    className={`${css.quickButton} ${count === val ? css.quickButtonActive : ''}`}
                    onClick={() => setQuickValue(val)}
                  >
                    {val} кг
                  </button>
                ))}
              </div> */}
              <div className={css.countControls}>
                <button
                  type="button"
                  className={css.countButton}
                  onClick={() => adjustCount(-0.1)}
                  disabled={(count || 0.1) <= 0.1}
                >
                  −
                </button>
                <input 
                  type="text"
                  inputMode="decimal"
                  placeholder="Количество (кг)"
                  value={countInput}
                  onChange={handleCountChange}
                  onBlur={handleCountBlur}
                  className={css.countInput}
                />
                <button
                  type="button"
                  className={css.countButton}
                  onClick={() => adjustCount(0.1)}
                >
                  +
                </button>
              </div>
            </>
          ) : (
            <div className={css.countControls}>
              <button
                type="button"
                className={css.countButton}
                onClick={() => adjustCount(-1)}
                disabled={(count || 1) <= 1}
              >
                −
              </button>
              <input 
                type="text"
                inputMode="numeric"
                placeholder="Количество (шт)"
                value={countInput}
                onChange={handleCountChange}
                onBlur={handleCountBlur}
                className={css.countInput}
              />
              <button
                type="button"
                className={css.countButton}
                onClick={() => adjustCount(1)}
              >
                +
              </button>
            </div>
          )}

          <button className={css.submit} onClick={addToCart}>Добавить в корзину</button>
        </div>
      </Popup>
    );
}

export default AddPopupProduct;